import { Box, FormControlLabel, styled, Typography } from '@mui/material';
import { StyledCustomTextField } from 'components/shared/PlanUnitSelector/PlanUnitSelector.styles';

/** The heading at the top of a toolbar popover, e.g. "Filters". */
export const BacklogPopoverTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary
}));

/** The two view-setting checkboxes, laid out side by side rather than stacked. */
export const BacklogCheckboxRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: theme.spacing(2)
}));

/**
 * One view-setting checkbox with its label. The theme sets a spacing(2) marginLeft on every form-control
 * label, meant for spaced-out forms; here it leaves too much air between the box and its word, so it is
 * pulled back to a tight 2px gap.
 */
export const ViewSettingCheckboxLabel = styled(FormControlLabel)(
  ({ theme }) => ({
    '& .MuiFormControlLabel-label': {
      marginLeft: theme.spacing(0.25)
    }
  })
);

export const BacklogPageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden'
});

export const BacklogTilesArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 4, 0, 4),
  minWidth: 0
}));

// Bounds the grid rather than scrolling itself: the grid handles its own scrolling so the week
// headers stay put. minHeight 0 lets it shrink inside the flex column instead of pushing out.
export const BacklogContentArea = styled(Box)({
  // Fills the toolbar's content slot and bounds the grid, which scrolls itself so the week headers
  // stay pinned. The outer padding now lives on BacklogToolbarArea.
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
  minHeight: 0,
  height: '100%'
});

export const BacklogStateArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(8)
}));

// Holds the shared ToolbarWrapper. It bounds the toolbar plus the grid, so the grid still gets a
// definite height and keeps its week headers pinned.
// A uniform spacing(4) gutter, so the toolbar card is not flush against the tiles above it. That is
// the same 16px ToolbarWrapper itself puts between the toolbar and its content (its container's
// gap), which leaves the card sitting in even space rather than in a gap chosen by eye.
export const BacklogToolbarArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  minWidth: 0,
  minHeight: 0,
  padding: theme.spacing(4)
}));

/**
 * Lays the two toolbar-control icons — filters and view settings — out side by side on the left of the
 * top toolbar, and stacked one above the other when the toolbar is docked to the right and runs
 * vertically. Each opens its own popper, so this only has to space the two buttons.
 */
export const BacklogSlotContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isVertical'
})<{ isVertical?: boolean }>(({ theme, isVertical }) => ({
  display: 'flex',
  flexDirection: isVertical ? 'column' : 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  minWidth: 0
}));

/**
 * Stacks a slot's controls when they are shown in the docked toolbar's popper instead of the top row.
 *
 * Just enough gap to keep the second field's floating label off the first field's border — outlined
 * inputs draw their label on the border itself, so flush fields read as cramped.
 */
export const BacklogControlStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0
}));

/**
 * One field in a toolbar slot: 200px wide, shrinking to a still-readable 160 before it wraps — narrower
 * than the 260 it started at, which crowded the date navigation.
 *
 * A width rather than `flex: '0 1 200px'`, deliberately. flex-basis sizes the MAIN axis, so that
 * shorthand meant 200px wide in the horizontal toolbar but 200px *tall* in the docked popper's column,
 * leaving each 40px field sitting in a 200px box. A width is the same thing on a row and the right axis
 * on a column; grow 0 and shrink 1 are the defaults the shorthand was spelling out anyway.
 */
export const BacklogFieldContainer = styled(Box)({
  width: 200,
  minWidth: 160
});

/**
 * The toolbar dropdowns, all three of them.
 *
 * isToolbar sets marginTop: 0 on the input, because the plan unit selector pairs it with hiddenLabel and
 * so needs no room above. These fields do show their label, so that room has to come back — 16px is what
 * a standard variant reserves by default — or the label sits on top of the value.
 *
 * The label is kept shrunk whatever the state. Left to itself it only floats up once the field is filled,
 * which made the two filters read at full size on the line while Weergave, always having a value, read
 * small and above: the same control looking like two different ones.
 */
export const BacklogToolbarField = styled(StyledCustomTextField)(
  ({ theme }) => ({
    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline': {
      // The plan unit selector's isToolbar pairs with hiddenLabel and so drops the space above the
      // input; these fields show their label, so the 16px a standard variant reserves is put back.
      marginTop: theme.spacing(2)
    }
  })
);
